<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';

import MessageMeta from '../MessageMeta.vue';
import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { getContrastingTextColor } from '@chatwoot/utils';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION } from '../constants';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

const props = defineProps({
  hideMeta: { type: Boolean, default: false },
  neutralSurface: { type: Boolean, default: false },
  metaPaddingClass: { type: String, default: '' },
});

const {
  variant,
  orientation,
  inReplyTo,
  shouldGroupWithNext,
  inboxId,
  contentAttributes,
} = useMessageContext();
const { t, te } = useI18n();
const inboxGetter = useMapGetter('inboxes/getInbox');

const inbox = computed(() => inboxGetter.value(inboxId.value) || {});
const DEFAULT_TEMPLATE_BACKGROUND_COLOR = '#2f80ed';
const temporaryMessageLabel = 'Mensagem temporária';
const smsMessageLabel = 'SMS';

const variantBaseMap = {
  [MESSAGE_VARIANTS.AGENT]:
    '!bg-n-brand !text-white [&_.prose-bubble]:!text-white [&_.prose-bubble_*]:!text-white',
  [MESSAGE_VARIANTS.PRIVATE]:
    'private-message bg-n-solid-amber border border-solid border-n-amber-6/60 text-n-amber-12 [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]:
    '!bg-n-slate-3 !text-n-slate-12 dark:!bg-n-slate-4 dark:!text-n-slate-11 [&_.prose-bubble]:!text-n-slate-12 dark:[&_.prose-bubble]:!text-n-slate-11 [&_.prose-bubble_*]:!text-n-slate-12 dark:[&_.prose-bubble_*]:!text-n-slate-11',
  [MESSAGE_VARIANTS.ACTIVITY]: 'bg-n-alpha-1 text-n-slate-11 text-sm',
  [MESSAGE_VARIANTS.BOT]:
    '!bg-[#1d1e24] !text-white dark:!bg-n-solid-2 [&_.prose-bubble]:!text-white [&_.prose-bubble_*]:!text-white',
  [MESSAGE_VARIANTS.CAMPAIGN]:
    'campaign-bubble !bg-[#4c1d95] !text-white border border-transparent [&_.prose-bubble]:!text-white [&_.prose-bubble_*]:!text-white',
  [MESSAGE_VARIANTS.TEMPLATE]:
    '!bg-[var(--message-bubble-bg)] !text-[var(--message-bubble-text)] [&_.prose-bubble]:!text-[var(--message-bubble-text)] [&_.prose-bubble_*]:!text-[var(--message-bubble-text)]',
  [MESSAGE_VARIANTS.ERROR]: 'bg-n-ruby-4 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-solid-amber/70 border border-dashed border-n-amber-12 text-n-amber-12',
};

const orientationMap = {
  [ORIENTATION.LEFT]:
    'left-bubble rounded-xl ltr:rounded-bl-sm rtl:rounded-br-sm',
  [ORIENTATION.RIGHT]:
    'right-bubble rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm',
  [ORIENTATION.CENTER]: 'rounded-md',
};

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const temporaryPrivateExpiresAt = computed(
  () =>
    contentAttributes.value?.expiresAt ||
    contentAttributes.value?.expires_at ||
    ''
);
const isTemporaryPrivateMessage = computed(
  () =>
    variant.value === MESSAGE_VARIANTS.PRIVATE &&
    Boolean(
      contentAttributes.value?.temporaryPrivate ||
        contentAttributes.value?.temporary_private
    ) &&
    Boolean(temporaryPrivateExpiresAt.value)
);
const currentTime = ref(Date.now());
let countdownTimer;

const remainingMilliseconds = computed(() =>
  Math.max(
    0,
    new Date(temporaryPrivateExpiresAt.value).getTime() - currentTime.value
  )
);
const expiryCountdown = computed(() => {
  const totalSeconds = Math.ceil(remainingMilliseconds.value / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) return `${days}d ${String(hours).padStart(2, '0')}h`;
  return [hours, minutes, seconds]
    .map(value => String(value).padStart(2, '0'))
    .join(':');
});

onMounted(() => {
  if (!isTemporaryPrivateMessage.value) return;

  currentTime.value = Date.now();
  countdownTimer = window.setInterval(() => {
    currentTime.value = Date.now();
  }, 1000);
});

onUnmounted(() => {
  if (countdownTimer) window.clearInterval(countdownTimer);
});

const messageClass = computed(() => {
  if (isTemporaryPrivateMessage.value) {
    return [
      'temporary-private-message border bg-n-ruby-2 text-n-ruby-12 dark:bg-n-ruby-3 [&_.prosemirror-mention-node]:font-semibold',
      orientationMap[orientation.value],
    ];
  }

  const neutralSurface =
    props.neutralSurface && variant.value !== MESSAGE_VARIANTS.PRIVATE;
  const classToApply = [
    neutralSurface
      ? '!bg-n-solid-2 !text-n-slate-12 border border-n-weak [&_.prose-bubble]:!text-n-slate-12 [&_.prose-bubble_*]:!text-n-slate-12'
      : variantBaseMap[variant.value],
  ];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[orientation.value]);
  } else {
    classToApply.push('rounded-lg');
  }

  return classToApply;
});

const messageStyle = computed(() => {
  if (variant.value !== MESSAGE_VARIANTS.TEMPLATE) return {};

  const widgetColor =
    inbox.value.channel_type === INBOX_TYPES.WEB
      ? inbox.value.widget_color
      : null;
  const backgroundColor = widgetColor || DEFAULT_TEMPLATE_BACKGROUND_COLOR;
  const textColor = widgetColor ? getContrastingTextColor(widgetColor) : '#fff';

  return {
    '--message-bubble-bg': backgroundColor,
    '--message-bubble-text': textColor,
  };
});

const scrollToMessage = () => {
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: inReplyTo.value.id,
  });
};

const shouldShowMeta = computed(
  () =>
    !props.hideMeta &&
    !shouldGroupWithNext.value &&
    variant.value !== MESSAGE_VARIANTS.ACTIVITY
);

const isSmsMessage = computed(
  () =>
    contentAttributes.value?.talkviaSms === true ||
    contentAttributes.value?.talkvia_sms === true
);

const reactions = computed(() => {
  const groupedReactions = new Map();
  const messageReactions = Array.isArray(contentAttributes.value?.reactions)
    ? contentAttributes.value.reactions
    : [];

  messageReactions.forEach(reaction => {
    const emoji =
      typeof reaction?.emoji === 'string' ? reaction.emoji.trim() : '';
    if (!emoji) return;

    groupedReactions.set(emoji, (groupedReactions.get(emoji) || 0) + 1);
  });

  return [...groupedReactions].map(([emoji, count]) => ({ emoji, count }));
});
const reactionAlignmentClass = computed(() =>
  orientation.value === ORIENTATION.RIGHT ? 'right-2' : 'left-2'
);
const reactionLabel = reaction =>
  reaction.count > 1
    ? `${reaction.count} reações ${reaction.emoji}`
    : `Reação ${reaction.emoji}`;

const metaClass = computed(() => {
  if (isTemporaryPrivateMessage.value) return 'text-n-ruby-12/60';
  if (variant.value === MESSAGE_VARIANTS.EMAIL) return 'px-4 pb-3';
  if (variant.value === MESSAGE_VARIANTS.PRIVATE) return 'text-n-amber-12/60';
  if (variant.value === MESSAGE_VARIANTS.BOT) return '!text-white/75';
  if (
    [
      MESSAGE_VARIANTS.AGENT,
      MESSAGE_VARIANTS.CAMPAIGN,
      MESSAGE_VARIANTS.TEMPLATE,
    ].includes(variant.value)
  ) {
    return '!text-white/75';
  }

  if (variant.value === MESSAGE_VARIANTS.USER)
    return '!text-n-slate-11 dark:!text-white/75';

  return 'text-n-slate-11';
});

const replyPreviewClass = computed(() => {
  const readableText =
    '[&_.prose-bubble]:!text-current [&_.prose-bubble_*]:!text-current [&_.prose-bubble]:!leading-relaxed [&_.prose-bubble_*]:!leading-relaxed';

  if (
    variant.value === MESSAGE_VARIANTS.PRIVATE &&
    !isTemporaryPrivateMessage.value
  ) {
    return [
      'bg-n-alpha-1 border-n-amber-7 text-n-amber-12 hover:bg-n-alpha-2',
      readableText,
    ];
  }

  if (variant.value === MESSAGE_VARIANTS.USER) {
    return [
      'bg-n-solid-1 border-n-strong text-n-slate-12 hover:bg-n-solid-2 dark:bg-n-solid-2 dark:text-n-slate-12 dark:hover:bg-n-solid-3',
      readableText,
    ];
  }

  if (variant.value === MESSAGE_VARIANTS.BOT) {
    return [
      'border-white/30 bg-white/10 text-white/85 hover:bg-white/15',
      readableText,
    ];
  }

  if (
    [
      MESSAGE_VARIANTS.AGENT,
      MESSAGE_VARIANTS.CAMPAIGN,
      MESSAGE_VARIANTS.TEMPLATE,
    ].includes(variant.value)
  ) {
    return [
      'bg-white/15 border-white/70 text-white/85 ring-1 ring-white/15 hover:bg-white/20',
      readableText,
    ];
  }

  return [
    'bg-black/10 border-white/40 text-white/85 hover:bg-black/15',
    readableText,
  ];
});

const replyToPreview = computed(() => {
  if (!inReplyTo) return '';

  const { content, attachments } = inReplyTo.value;

  if (content) return new MessageFormatter(content).formattedMessage;
  if (attachments?.length) {
    const firstAttachment = attachments[0];
    const fileType = firstAttachment.fileType ?? firstAttachment.file_type;

    const key = `CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`;
    return t(te(key) ? key : 'CHAT_LIST.ATTACHMENTS.file.CONTENT');
  }

  return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});
</script>

<template>
  <div
    class="text-sm min-w-0 relative"
    :class="[
      messageClass,
      {
        'max-w-lg': variant !== MESSAGE_VARIANTS.EMAIL,
        'mb-2.5': reactions.length,
      },
    ]"
    :style="messageStyle"
  >
    <div>
      <div
        v-if="
          variant === MESSAGE_VARIANTS.PRIVATE && !isTemporaryPrivateMessage
        "
        class="private-message__label mb-2 flex items-center gap-1.5 text-[11px] font-medium leading-4 text-n-amber-12"
      >
        <span
          class="i-hugeicons-square-lock-02 size-3.5 shrink-0"
          aria-hidden="true"
        />
        <span>{{ t('CONVERSATION.PRIVATE_NOTE_LABEL') }}</span>
      </div>
      <div
        v-if="isSmsMessage"
        class="mb-2 inline-flex items-center gap-1.5 rounded-full border border-current/20 bg-black/10 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide"
      >
        <span class="i-lucide-message-square-text size-3.5" />
        <span>{{ smsMessageLabel }}</span>
      </div>
      <div
        v-if="isTemporaryPrivateMessage"
        class="temporary-private-message__timer mb-2.5 flex items-center justify-between gap-3 rounded-lg px-2.5 py-2"
      >
        <span class="inline-flex items-center gap-2 text-xs font-semibold">
          <span class="i-lucide-bomb size-4" />
          <span>{{ temporaryMessageLabel }}</span>
        </span>
        <span
          class="inline-flex items-center gap-1.5 rounded-full bg-n-ruby-4 px-2 py-1 font-mono text-[11px] font-semibold tabular-nums text-n-ruby-11"
        >
          <span class="i-lucide-clock-3 size-3" />
          {{ expiryCountdown }}
        </span>
      </div>
      <div
        v-if="inReplyTo"
        class="mb-2.5 rounded-lg border-l-2 px-3 py-2 shadow-sm cursor-pointer transition-colors"
        :class="replyPreviewClass"
        @click="scrollToMessage"
      >
        <div
          v-dompurify-html="replyToPreview"
          class="prose prose-bubble line-clamp-2 text-xs"
        />
      </div>
      <slot />
      <MessageMeta
        v-if="shouldShowMeta"
        :class="[flexOrientationClass, metaClass, props.metaPaddingClass]"
        class="mt-2"
      />
    </div>
    <div
      v-if="reactions.length"
      class="absolute -bottom-2.5 z-20 flex items-center gap-1"
      :class="reactionAlignmentClass"
    >
      <span
        v-for="reaction in reactions"
        :key="reaction.emoji"
        class="inline-flex h-6 min-w-7 items-center justify-center gap-1 rounded-full bg-n-solid-1 px-1.5 text-[13px] leading-none text-n-slate-12 shadow-[0_1px_3px_rgba(15,23,42,0.16)] dark:bg-n-solid-3"
        :aria-label="reactionLabel(reaction)"
        :title="reactionLabel(reaction)"
      >
        <span>{{ reaction.emoji }}</span>
        <span
          v-if="reaction.count > 1"
          class="text-[10px] font-semibold tabular-nums text-n-slate-10"
        >
          {{ reaction.count }}
        </span>
      </span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.private-message {
  border-radius: 12px;
  border-inline-start-width: 2px;
}

.private-message :deep(.expandable-content-toggle) {
  @apply rounded-md bg-n-alpha-1 px-2 py-1 text-xs font-medium text-n-amber-12 no-underline transition-colors hover:bg-n-alpha-2;
}

.temporary-private-message {
  position: relative;
  overflow: hidden;
  isolation: isolate;
  background-image: radial-gradient(
    circle at 100% 0%,
    rgb(244 63 94 / 16%),
    transparent 48%
  );
  border-color: rgb(244 63 94 / 42%);
  box-shadow:
    0 0 0 1px rgb(244 63 94 / 8%),
    0 10px 28px rgb(159 18 57 / 10%);
}

.temporary-private-message::before,
.temporary-private-message::after {
  position: absolute;
  pointer-events: none;
  content: '';
  z-index: 2;
}

.temporary-private-message::before {
  inset: 0;
  padding: 2px;
  border-radius: inherit;
  background: repeating-linear-gradient(
    115deg,
    rgb(244 63 94 / 38%) 0 7px,
    rgb(251 146 60 / 85%) 7px 10px,
    rgb(253 224 71 / 95%) 10px 12px,
    rgb(244 63 94 / 38%) 12px 19px
  );
  background-size: 40px 40px;
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  animation: temporary-private-fuse 1.8s linear infinite;
}

.temporary-private-message::after {
  top: 0;
  left: 0;
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: rgb(254 240 138);
  box-shadow:
    0 0 3px 1px rgb(255 255 255 / 90%),
    0 0 8px 3px rgb(251 146 60 / 90%),
    0 0 14px 5px rgb(244 63 94 / 55%);
  offset-path: inset(3px round 11px);
  animation: temporary-private-ember 4s linear infinite;
}

.temporary-private-message__timer {
  border: 1px solid rgb(244 63 94 / 20%);
  background: rgb(244 63 94 / 8%);
}

@keyframes temporary-private-fuse {
  to {
    background-position: 40px 0;
  }
}

@keyframes temporary-private-ember {
  to {
    offset-distance: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .temporary-private-message::before,
  .temporary-private-message::after {
    animation: none;
  }
}

.campaign-bubble {
  box-shadow:
    0 0 0 1px rgb(167 139 250 / 0.26),
    0 12px 26px rgb(124 58 237 / 0.16),
    inset 0 0 16px rgb(196 181 253 / 0.12);
}
</style>
